// The invalidation map — which cached reads each mutation makes stale, as data (ARCH §9.3; T-023).
//
//   await client.requestSync(accountId);
//   await queryClient.invalidateQueries(invalidation.requestSync(accountId));
//
// `INVALIDATION` names key families (`keys.ts`) per `ApiClient` mutation; `invalidation.<mutation>(accountId)`
// turns that into one `InvalidateQueryFilters` (a `queryKey` prefix for one family, a `predicate` over the
// prefixes for several). A test keeps the map complete: every non-`get*` method of `ApiClient` — the
// generated contract — must have a row, so a new endpoint fails the build until its row is written.
//
// Reading the rows: `account` is the whole namespace — a mutation that changes the account's `dataVersion`
// (a refresh, a reset, a publish) may change every read, and TanStack refetches only the queries a screen is
// observing, so the broad row costs nothing. The three POSTs that compute without side effects
// (`validateChange`, `previewChanges`, `backtestRule` — API §5, §8) invalidate nothing.

import type { InvalidateQueryFilters, QueryKey } from '@tanstack/query-core';
import { familyKey, type KeyFamily } from './keys';

export const INVALIDATION = {
  // -- session, sync, demo controls (API §2) ------------------------------------------------------------------
  requestSync: ['account'], // the rebase bumps meta.dataVersion: summary, timeseries, entities, … all move
  resetDemo: ['account', 'session'], // the full story is back: everything, and the session's account list
  startFresh: ['account', 'session'], // demoVariant, plan, rules, inbox, staged changes, first-run
  setFaults: ['account'], // a fault changes the meta / state of every read (stale, partial, rate-limited)
  setViewOnlyPreview: ['accountInfo', 'session', 'changes'], // mode + capabilities; publish now refused

  // -- Home extras (API §3.3–3.7; T-168) -------------------------------------------------------------------------
  // The briefing is composed from the inbox, the flags, the month and the rows (ENGINE §12), so it is invalidated
  // by its own two writes AND by every write below that can move one of those — the rows are named per mutation
  // rather than by a family, because `briefing` is its own key outside `alerts` / `spend`.
  dismissBriefingItem: ['briefing'],
  markVisit: ['briefing'], // "since your last visit" moves
  // presets 1 / 3 / 6 switched on and evaluated at once, the plan adopted (pacing, the guardrail), the log's row,
  // the shell's unread count — and the briefing, whose Going well now holds what the evaluation raised (Dana 2);
  // `session` beside `accountInfo` (D-301 / D-507, T-290): the badge reads the session's `AccountInfo`, and Dana 2's
  // badge stayed at 0 until a reload on both builds until each invalidated `queries.session()` by hand
  completeFirstRun: [
    'firstRun',
    'alerts',
    'spend',
    'activity',
    'accountInfo',
    'session',
    'briefing',
  ],

  // -- Spend & Budget (API §5) ----------------------------------------------------------------------------------
  // A plan or guardrail change evaluates the rules at once (REQ-174 AC6) and switches on a preset that was
  // waiting for a plan (REQ-172 AC4, T-166), so the alert family and the shell's unread count move with it; the
  // guardrail is also the review screen's `guardrail_exceeded` line (§6.5 reads the plan in force) and the
  // briefing's pacing / overspend item (T-168), so `changes` and `briefing` go too.
  putSpendPlan: [
    'spend',
    'activity',
    'firstRun',
    'alerts',
    'accountInfo',
    'session',
    'changes',
    'briefing',
  ], // pacing / forecast; logged; planToAdopt becomes null; `session` because the evaluation it triggers can move `unreadAlerts` (D-301, T-290)
  dismissWasted: ['spend', 'entities', 'briefing'], // the flag leaves the wasted list, the rows and the briefing's candidates

  // -- Change-sets and activity (API §6) ------------------------------------------------------------------------
  // The tray is server state (ARCH §9.3), so a write to it makes three reads stale and **no others**:
  // `changes` (the open set and its preview), `accountInfo` (`stagedChanges`, the shell pill) and the pacing
  // chart's staged forecast (REQ-072 AC3 — `?withStaged=true` is computed from the stored set). The entity
  // rows are deliberately NOT here: the staged marker is merged onto them client-side from the change-set
  // query (`withStagedMarkers` in `@sl/core/rows`), which is what "lists are not refetched when something is
  // staged" means (REQ-101 AC1, T-129 / T-138's Accept). `spendPacing` rather than the whole `spend` family,
  // because the plan, the allocation map and Possibly wasted do not know the tray exists.
  validateChange: [], // side-effect-free POST
  stageChange: ['changes', 'accountInfo', 'spendPacing'],
  removeChange: ['changes', 'accountInfo', 'spendPacing'],
  discardChanges: ['changes', 'accountInfo', 'spendPacing'],
  previewChanges: [], // side-effect-free POST
  publishChanges: ['account'], // budgets / statuses changed on the platform: rows, sums, markers, log, counts
  undoPublish: ['account'],
  markDone: ['changes', 'accountInfo', 'activity'],
  revertActivity: ['changes', 'accountInfo', 'activity', 'spendPacing'], // stages the reverting change

  // -- Alerts (API §9) ------------------------------------------------------------------------------------------
  // Every rule write is evaluated AT ONCE (REQ-174 AC6): the presets' `enabledRuleId`, the rules list, the
  // builder's `rulesUsed` and the inbox may all have moved, so the whole `alerts` family goes, and `accountInfo`
  // with it (`unreadAlerts`). The back-tests under `alerts` are keyed on their draft and stay correct across a
  // rule write — they are invalidated by the family too, which only costs a recompute the server caches (T-166).
  // Every one of them can open or close an inbox row, which is a briefing candidate (T-168) and, for a Heads up
  // row, an entity's verdict (REQ-040 AC2's open-alert clause) — so `briefing` and `entities` ride along.
  // …and `session` beside `accountInfo` on every row that moves `unreadAlerts` (D-301, D-406 — T-286): the shell's
  // badge reads the session's `AccountInfo`, which is the one `AccountInfo` either app observes; without the row both
  // builds invalidated `queries.session()` by hand after each of these writes.
  enablePreset: ['alerts', 'firstRun', 'accountInfo', 'session', 'briefing', 'entities'], // rules; recommendedAlertsAlreadyOn; unreadAlerts
  createRule: ['alerts', 'firstRun', 'accountInfo', 'session', 'briefing', 'entities'],
  updateRule: ['alerts', 'accountInfo', 'session', 'briefing', 'entities'],
  patchRule: ['alerts', 'accountInfo', 'session', 'briefing', 'entities'], // switching on evaluates at once → unreadAlerts may move
  deleteRule: ['alerts', 'accountInfo', 'session', 'briefing', 'entities'], // soft delete closes the rule's open alerts → unreadAlerts may move
  backtestRule: [], // side-effect-free POST
  // The §9.4 writes (T-167) move ROWS, not rules: the inbox family (both views, the detail) and the shell count go,
  // the gallery and the draft-keyed back-tests stay. A snooze is the one that also lives on a rule (`snoozes[]`),
  // and both snooze writes evaluate at once — the rules list's "last fired" may move — so they take `alertRules` too.
  // A row that leaves the inbox (snoozed, dismissed) or comes back (unsnoozed) is a briefing candidate that moved
  // and, for a Heads up row, a verdict that moved (T-168) — read / read-all move nothing but the state.
  markAlertRead: ['alertInbox', 'accountInfo', 'session'], // unreadAlerts
  markAllAlertsRead: ['alertInbox', 'accountInfo', 'session'],
  snoozeAlert: ['alertInbox', 'alertRules', 'accountInfo', 'session', 'briefing', 'entities'],
  removeSnooze: ['alertInbox', 'alertRules', 'accountInfo', 'session', 'briefing', 'entities'],
  dismissAlert: ['alertInbox', 'accountInfo', 'session', 'briefing'],
  // …and acting is STAGING (D-013): the inbox family plus exactly what `stageChange` invalidates — the tray, the
  // shell pill and the staged forecast — so the tray and the tree's markers update the way an edit's do. Plus (T-290,
  // T-171 / T-179 / T-181): `briefing`, because an act changes the panel (the item's action is done, its alert reads
  // `acted`; the sticky "since" of D-440 is what kept the staleness invisible), and `entities`, because acting on an
  // alert changes an entity's open-alert set, which D-291 makes part of its verdict (REQ-040 AC2's open-alert clause).
  runAlertAction: ['alertInbox', 'changes', 'accountInfo', 'spendPacing', 'briefing', 'entities'],

  // -- Settings and preferences (API §10) ------------------------------------------------------------------------
  // …and the briefing's best value and first-run's cards read the primary result and the counted results (T-168)
  putSettings: [
    'settings',
    'summary',
    'timeseries',
    'entities',
    'spend',
    'creatives',
    'activity',
    'briefing',
    'firstRun',
  ], // attribution / overrides / primary result re-sum everything; logged + marker
  putPreferences: ['preferences'],
} as const satisfies Record<string, readonly KeyFamily[]>;

export type MutationName = keyof typeof INVALIDATION;
export const MUTATION_NAMES = Object.keys(INVALIDATION) as MutationName[];

/** TanStack's partial key match (`partialMatchKey`), mirrored so this module needs no runtime import:
 *  `prefix` matches `key` when every element of `prefix` deep-partially matches the same position of `key`. */
export function isKeyPrefix(key: unknown, prefix: unknown): boolean {
  if (key === prefix) return true;
  if (typeof key !== typeof prefix) return false;
  if (key === null || prefix === null || typeof key !== 'object' || typeof prefix !== 'object')
    return false;
  const k = key as Record<string, unknown>;
  const p = prefix as Record<string, unknown>;
  return Object.keys(p).every((name) => isKeyPrefix(k[name], p[name]));
}

/** The key prefixes a mutation invalidates for an account. */
export function invalidatedKeys(mutation: MutationName, accountId: string): readonly QueryKey[] {
  return INVALIDATION[mutation].map((family) => familyKey(family, accountId));
}

/** One `InvalidateQueryFilters` per mutation: `queryClient.invalidateQueries(invalidation.requestSync(id))`. */
export function invalidationFilters(
  mutation: MutationName,
  accountId: string,
): InvalidateQueryFilters {
  const keys = invalidatedKeys(mutation, accountId);
  const [first] = keys;
  if (first === undefined) return { predicate: () => false };
  if (keys.length === 1) return { queryKey: first };
  return {
    predicate: (query: { queryKey: QueryKey }) => keys.some((k) => isKeyPrefix(query.queryKey, k)),
  };
}

export const invalidation: Readonly<
  Record<MutationName, (accountId: string) => InvalidateQueryFilters>
> = Object.fromEntries(
  MUTATION_NAMES.map((m) => [m, (accountId: string) => invalidationFilters(m, accountId)]),
) as Record<MutationName, (accountId: string) => InvalidateQueryFilters>;
